import baseConfig from './baseConfig'
import {
  OFFER_TYPE_CRUISE_SLUG,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
  OFFER_TYPE_EXPERIENCE_SLUG,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_CARHIRE_SLUG,
} from 'constants/offer'

import { LE_CRUISE_PRODUCT_LABEL, LE_FLASH_HOTEL_PRODUCT_LABEL, LE_LME_HOTEL_PRODUCT_LABEL, LE_LPP_PRODUCT_LABEL, LE_TAO_HOTEL_PRODUCT_LABEL, LE_TOUR_PRODUCT_LABEL, LE_TOUR_V2_PRODUCT_LABEL } from 'constants/productLabel'
// oxlint-disable-next-line no-restricted-imports
import { leAgentHubBorderRadii, leAgentHubShadows, leAgenthubDefaultPalette, leAgenthubInversePalette } from 'constants/themes/leagenthub'
import { leConfigMessages, leAgentHubConfigMessages, staticPageMessages, howItWorksMessages } from './i18nMessages'

// Bali is enforced as Indonesia country-wide in Agent Hub hotel bundled flights.
export const LE_AGENT_HUB_HOTEL_BUNDLED_FLIGHT_ALLOWED_COUNTRIES = [
  'indonesia',
  'singapore',
  'vietnam',
  'malaysia',
  'maldives',
  'new zealand',
  'hong kong',
  'japan',
] as const

/** IATA codes for `filters.airlines` on standalone `/flights-search*` flight search (Agent Hub). */
export const LE_AGENT_HUB_FLIGHT_SEARCH_ALLOWED_AIRLINE_CODES = [
  'SQ',
  'MH',
  'QR',
  'EK',
  'NZ',
  'FJ',
  'VN',
  'JQ',
  'GA',
  'CX',
  'L0',
] as const

export default function getConfig(env: App.EnvVarsAndFeatures): App.AppConfig {
  return ({
    ...baseConfig,
    ...env,
    theme: {
      defaultPalette: leAgenthubDefaultPalette,
      inversePalette: leAgenthubInversePalette,
      shadows: leAgentHubShadows,
      borderRadius: leAgentHubBorderRadii,
    },
    regions: [],
    currencyConfig: {},
    multiCurrency: true,
    brandConfig: {
      ...baseConfig.brandConfig,
      name: 'LE Agent Hub',
      purchaseSynonym: 'escape',
      adventureSynonym: 'escape',
      purchasesTerm: 'escapes',
      brandHoliday: 'Luxury Escape',
      subscriptionParentBrand: 'leagenthub',
    },

    productTypeLabelMap: {
      [OFFER_TYPE_HOTEL]: LE_FLASH_HOTEL_PRODUCT_LABEL,
      [OFFER_TYPE_LAST_MINUTE]: LE_LME_HOTEL_PRODUCT_LABEL,
      [OFFER_TYPE_ALWAYS_ON]: LE_TAO_HOTEL_PRODUCT_LABEL,
      [OFFER_TYPE_BED_BANK]: LE_LPP_PRODUCT_LABEL,
      [OFFER_TYPE_TOUR]: LE_TOUR_PRODUCT_LABEL,
      [OFFER_TYPE_TOUR_V2]: LE_TOUR_V2_PRODUCT_LABEL,
      [OFFER_TYPE_CRUISE]: LE_CRUISE_PRODUCT_LABEL,
    },
    title: 'LE Agent Hub',
    notification: true,
    offerList: {
      lightFilter: false,
      howItWorks: {
        showToLoggedIn: false,
        [OFFER_TYPE_TOUR]: [{
          icon: 'handpicked-partner',
          title: howItWorksMessages.tourBestPriceGuaranteeTitle,
          content: howItWorksMessages.tourBestPriceGuaranteeContent,
          iconSize: 48,
          slug: 'best-price-guarantee',
          contentSize: 'large',
        }, {
          icon: 'hotel',
          title: howItWorksMessages.tourHandpickedPartnersTitle,
          content: howItWorksMessages.tourHandpickedPartnersContent,
          iconSize: 48,
          contentSize: 'large',
        }, {
          icon: 'map',
          title: howItWorksMessages.tourCuratedByExpertsTitle,
          content: howItWorksMessages.tourCuratedByExpertsContent,
          iconSize: 48,
          contentSize: 'large',
        }],
        [OFFER_TYPE_HOTEL]: [{
          icon: 'handpicked-partner',
          title: howItWorksMessages.agentHubHotelBestPriceTitle,
          content: howItWorksMessages.hotelWePriceMatchContent,
          iconSize: 48,
          slug: 'best-price-guarantee',
        }, {
          icon: 'refund',
          title: howItWorksMessages.hotelChangeOfMindRefundTitle,
          content: howItWorksMessages.hotelChangeOfMindRefundContent,
          iconSize: 48,
        }, {
          icon: 'vip-inclusions',
          title: howItWorksMessages.hotelVipInclusionsTitle,
          content: howItWorksMessages.hotelVipInclusionsContent,
          iconSize: 48,
        }],
        [OFFER_TYPE_LAST_MINUTE]: [{
          icon: 'handpicked-partner',
          title: howItWorksMessages.lmhUnbeatableValueTitle,
          content: howItWorksMessages.lmhUnbeatableValueContent,
          iconSize: 48,
          iconType: 'eighth',
        }, {
          icon: 'vip-inclusions',
          title: howItWorksMessages.lmhExclusiveExtrasTitle,
          content: howItWorksMessages.lmhExclusiveExtrasContent,
          iconSize: 48,
          iconType: 'eighth',
        }, {
          icon: 'hotel',
          title: howItWorksMessages.lmhHandPickedHotelsTitle,
          content: howItWorksMessages.lmhHandPickedHotelsContent,
          iconSize: 48,
          iconType: 'eighth',
        }, {
          icon: 'calendarIcon',
          title: howItWorksMessages.lmhInstantBookingTitle,
          content: howItWorksMessages.lmhInstantBookingContent,
          iconSize: 48,
          iconType: 'eighth',
        }],
      },
    },
    header: {
      height: {
        sm: 71,
        lg: 119,
      },
      checkout: {
        height: 71,
      },
      searchBarText: leConfigMessages.searchBarText,
      showHelp: false,
      showPhoneOnCheckout: false,
      standAloneAccount: false,
      logo: {
        to: '/',
      },
      emailPreferences: {
        to: '/account/communication-preferences',
      },
      nav: {
        activeDecoration: false,
        strong: false,
        items: [
          {
            to: `/${OFFER_TYPE_HOTEL_SLUG}`,
            id: `nav-link-${OFFER_TYPE_HOTEL_SLUG}`,
            text: 'Hotels',
            testId: 'nav-link-hotels',
            iconName: 'bed',
            mobileNav: true,
            category: 'primary',
          },
          {
            to: '/flights',
            id: 'nav-link-flights',
            text: 'Direct Maldives Flights',
            testId: 'nav-link-flights',
            onlyOnRegions: env.STANDALONE_FLIGHT_REGIONS.split(','),
            mobileNav: true,
            iconName: 'plane-departure',
            featureToggle: 'ENABLE_STANDALONE_FLIGHTS',
            category: 'primary',
            newLabel: true,
          },
          {
            to: `/${OFFER_TYPE_TOUR_SLUG}`,
            id: `nav-link-${OFFER_TYPE_TOUR_SLUG}`,
            text: 'Tours',
            testId: 'nav-link-tours',
            mobileNav: true,
            iconName: 'compass',
            category: 'primary',
          },
          {
            to: `/${PRODUCT_TYPE_ULTRALUX_SLUG}`,
            id: 'nav-link-ultralux',
            text: 'Ultra Lux',
            mobileNav: true,
            testId: 'nav-link-ultralux',
            featureToggle: 'ULTRALUX_ENABLED',
            iconName: 'ultra-lux-diamond',
            category: 'primary',
          },
          {
            to: `/${OFFER_TYPE_CRUISE_SLUG}`,
            id: `nav-link-${OFFER_TYPE_CRUISE_SLUG}`,
            text: 'Cruises',
            onlyOnRegions: env.STANDALONE_CRUISE_REGIONS?.split(','),
            mobileNav: true,
            testId: 'nav-link-cruises',
            featureToggle: 'CRUISES_STANDLONE_ENABLED',
            iconName: 'ship',
            category: 'primary',
          },
          {
            to: `/${OFFER_TYPE_EXPERIENCE_SLUG}`,
            id: `nav-link-${OFFER_TYPE_EXPERIENCE_SLUG}`,
            text: 'Experiences',
            mobileNav: true,
            iconName: 'hot-air-balloon',
            testId: `nav-link-${OFFER_TYPE_EXPERIENCE_SLUG}`,
            featureToggle: 'EXPERIENCES_ENABLED',
            category: 'primary',
          },
          {
            to: `/${OFFER_TYPE_CARHIRE_SLUG}`,
            id: `nav-link-${OFFER_TYPE_CARHIRE_SLUG}`,
            text: 'Car Hire',
            testId: 'nav-link-car-hire',
            onlyOnRegions: env.CARHIRE_REGIONS.split(','),
            mobileNav: true,
            iconName: 'car',
            featureToggle: 'CARHIRE_ENABLED',
            category: 'primary',
          },
        ],
      },
    },
    searchVerticalItems: [
      { label: 'hotels', vertical: 'hotels' },
      { label: 'tours', vertical: 'tours' },
      { label: 'cruises', vertical: 'cruises' },
      { label: 'experiences', vertical: 'experiences' },
      { label: 'car hire', vertical: 'car-hire' },
      { label: 'flights', vertical: 'flights' },
    ],
    footer: {
      type: 'original',
      partners: [{
        name: 'Luxury Escapes',
        href: 'https://luxuryescapes.com/au',
      }],
    },
    searchFormSection: {
      imageId: 'lgh2aaa5qtscfbipahq',
      hotelsImageId: '8d2cc64monviv73whp4',
      escapeSynonym: 'escape',
      leads: [
        'Curated escapes with unbeatable value for destinations all over the world',
        'Unforgettable travel experiences with handpicked inclusions',
        'Luxury hotels, resorts and tours launch daily',
      ],
      title: leConfigMessages.searchFormTitle,
    },
    hasMagazine: false,
    destinationPages: false,
    contentHub: false,
    holidayPages: false,
    contactForm: false,
    topDestinationList: {
      countries: [
        {
          name: 'Australia',
          level: 'country',
        },
        {
          name: 'United States',
          level: 'country',
        },
        {
          name: 'Indonesia',
          level: 'country',
        },
        {
          name: 'Thailand',
          level: 'country',
        },
        {
          name: 'United Kingdom',
          level: 'country',
        },
        {
          name: 'Spain',
          level: 'country',
        },
        {
          name: 'Malaysia',
          level: 'country',
        },
        {
          name: 'Italy',
          level: 'country',
        },
        {
          name: 'Portugal',
          level: 'country',
        },
        {
          name: 'France',
          level: 'country',
        },
        {
          name: 'New Zealand',
          level: 'country',
        },
        {
          name: 'Mexico',
          level: 'country',
        },
        {
          name: 'United Arab Emirates',
          level: 'country',
        },
        {
          name: 'Fiji',
          level: 'country',
        },
        {
          name: 'Greece',
          level: 'country',
        },
        {
          name: 'Maldives',
          level: 'country',
        },
        {
          name: 'Vietnam',
          level: 'country',
        },
        {
          name: 'Netherlands',
          level: 'country',
        },
        {
          name: 'Cook Islands',
          level: 'country',
        },
        {
          name: 'Mauritius',
          level: 'country',
        },
        {
          name: 'Malta',
          level: 'country',
        },
        {
          name: 'Iceland',
          level: 'country',
        },
        {
          name: 'Philippines',
          level: 'country',
        },
        {
          name: 'Germany',
          level: 'country',
        },
        {
          name: 'Japan',
          level: 'country',
        },
        {
          name: 'Hong Kong',
          level: 'country',
        },
        {
          name: 'Cambodia',
          level: 'country',
        },
        {
          name: 'Morocco',
          level: 'country',
        },
        {
          name: 'Switzerland',
          level: 'country',
        },
        {
          name: 'South Africa',
          level: 'country',
        },
        {
          name: 'Jamaica',
          level: 'country',
        },
        {
          name: 'Oman',
          level: 'country',
        },
        {
          name: 'Singapore',
          level: 'country',
        },
        {
          name: 'The Bahamas',
          level: 'country',
        },
        {
          name: 'Saint Lucia',
          level: 'country',
        },
        {
          name: 'Turkey',
          level: 'country',
        },
        {
          name: 'Sri Lanka',
          level: 'country',
        },
        {
          name: 'Dominican Republic',
          level: 'country',
        },
        {
          name: 'Croatia',
          level: 'country',
        },
        {
          name: 'Montenegro',
          level: 'country',
        },
        {
          name: 'Ireland',
          level: 'country',
        },
        {
          name: 'Czechia',
          level: 'country',
        },
        {
          name: 'South Korea',
          level: 'country',
        },
        {
          name: 'Bermuda',
          level: 'country',
        },
        {
          name: 'Vanuatu',
          level: 'country',
        },
        {
          name: 'Austria',
          level: 'country',
        },
        {
          name: 'Hungary',
          level: 'country',
        },
        {
          name: 'Tanzania',
          level: 'country',
        },
        {
          name: 'India',
          level: 'country',
        },
        {
          name: 'Anguilla',
          level: 'country',
        },
        {
          name: 'China',
          level: 'country',
        },
        {
          name: 'Colombia',
          level: 'country',
        },
        {
          name: 'Botswana',
          level: 'country',
        },
        {
          name: 'Zimbabwe',
          level: 'country',
        },
        {
          name: 'Brazil',
          level: 'country',
        },
        {
          name: 'Poland',
          level: 'country',
        },
        {
          name: 'Israel',
          level: 'country',
        },
        {
          name: 'Costa Rica',
          level: 'country',
        },
        {
          name: 'Antigua and Barbuda',
          level: 'country',
        },
        {
          name: 'Saudi Arabia',
          level: 'country',
        },
        {
          name: 'Seychelles',
          level: 'country',
        },
        {
          name: 'Samoa',
          level: 'country',
        },
        {
          name: 'Peru',
          level: 'country',
        },
        {
          name: 'Laos',
          level: 'country',
        },
        {
          name: 'Canada',
          level: 'country',
        },
        {
          name: 'Macao',
          level: 'country',
        },
        {
          name: 'Qatar',
          level: 'country',
        },
        {
          name: 'Rwanda',
          level: 'country',
        },
        {
          name: 'Sweden',
          level: 'country',
        },
      ],
      cities: [
        {
          name: 'Melbourne',
          level: 'location',
          higherLevel: 'Victoria',
          country: 'Australia',
        },
        {
          name: 'Sydney',
          level: 'location',
          higherLevel: 'New South Wales',
          country: 'Australia',
        },
        {
          name: 'Perth',
          level: 'location',
          higherLevel: 'Western Australia',
          country: 'Australia',
        },
        {
          name: 'Brisbane',
          level: 'location',
          higherLevel: 'Queensland',
          country: 'Australia',
        },
        {
          name: 'Gold Coast',
          level: 'location',
          higherLevel: 'Queensland',
          country: 'Australia',
        },
        {
          name: 'Bangkok',
          level: 'location',
          higherLevel: 'Bangkok',
          country: 'Thailand',
        },
        {
          name: 'Krabi',
          level: 'location',
          higherLevel: '',
          country: 'Thailand',
        },
        {
          name: 'Chiang Mai',
          level: 'location',
          higherLevel: 'Chiang Mai',
          country: 'Thailand',
        },
        {
          name: 'Chiang Rai',
          level: 'location',
          higherLevel: '',
          country: 'Thailand',
        },
        {
          name: 'Ubud',
          level: 'location',
          higherLevel: 'Bali',
          country: 'Indonesia',
        },
        {
          name: 'Kuala Lumpur',
          level: 'location',
          higherLevel: 'Federal Territory Of Kuala Lumpur',
          country: 'Malaysia',
        },
        {
          name: 'San Francisco',
          level: 'location',
          higherLevel: 'California',
          country: 'United States',
        },
        {
          name: 'Tokyo',
          level: 'location',
          higherLevel: 'Tokyo',
          country: 'Japan',
        },
        {
          name: 'Paris',
          level: 'location',
          higherLevel: 'Île-De-France',
          country: 'France',
        },
        {
          name: 'Los Angeles',
          level: 'location',
          higherLevel: 'California',
          country: 'United States',
        },
        {
          name: 'Rome',
          level: 'location',
          alternativeNameForLink: 'Lazio',
          higherLevel: '',
          country: 'Italy',
        },
        {
          name: 'London',
          level: 'location',
          higherLevel: 'England',
          country: 'United Kingdom',
        },
        {
          name: 'Hong Kong',
          level: 'location',
          higherLevel: '',
          alternativeNameForLink: 'Hong Kong Island',
          country: 'Hong Kong',
        },
        {
          name: 'Auckland',
          level: 'location',
          higherLevel: 'Auckland',
          country: 'New Zealand',
        },
        {
          name: 'Port Douglas',
          level: 'location',
          higherLevel: 'Queensland',
          country: 'Australia',
        },
        {
          name: 'New York',
          level: 'location',
          higherLevel: 'New York',
          country: 'United States',
        },
        {
          name: 'Langkawi',
          level: 'location',
          higherLevel: 'Kedah',
          country: 'Malaysia',
        },
        {
          name: 'Ho Chi Minh',
          level: 'location',
          alternativeNameForLink: 'Ho Chi Minh City',
          higherLevel: 'Ho Chi Minh City',
          country: 'Vietnam',
        },
        {
          name: 'Amsterdam',
          level: 'location',
          higherLevel: 'North Holland',
          country: 'Netherlands',
        },
        {
          name: 'Barcelona',
          level: 'location',
          higherLevel: 'Catalonia',
          country: 'Spain',
        },
        {
          name: 'Canberra',
          level: 'location',
          higherLevel: 'Australian Capital Territory',
          country: 'Australia',
        },
        {
          name: 'Darwin',
          level: 'location',
          higherLevel: 'Northern Territory',
          country: 'Australia',
        },
        {
          name: 'Hobart',
          level: 'location',
          higherLevel: 'Tasmania',
          country: 'Australia',
        },
        {
          name: 'Hanoi',
          level: 'location',
          higherLevel: 'Hanoi',
          country: 'Vietnam',
        },
        {
          name: 'Queenstown',
          level: 'location',
          higherLevel: 'Otago',
          country: 'New Zealand',
        },
        {
          name: 'Shanghai',
          level: 'location',
          higherLevel: 'Shanghai',
          country: 'China',
        },
        {
          name: 'Venice',
          level: 'location',
          alternativeNameForLink: 'Veneto',
          higherLevel: '',
          country: 'Italy',
        },
        {
          name: 'Berlin',
          level: 'location',
          higherLevel: 'Berlin',
          country: 'Germany',
        },
        {
          name: 'Taupo',
          level: 'location',
          alternativeNameForLink: 'Taupō',
          higherLevel: 'Waikato',
          country: 'New Zealand',
        },
        {
          name: 'Rotarua',
          level: 'location',
          higherLevel: 'Bay Of Plenty',
          country: 'New Zealand',
        },
        {
          name: 'Milan',
          level: 'location',
          alternativeNameForLink: 'Lombardy',
          higherLevel: '',
          country: 'Italy',
        },
        {
          name: 'Madrid',
          level: 'location',
          higherLevel: 'Community Of Madrid',
          country: 'Spain',
        },
        {
          name: 'Launceston',
          level: 'location',
          higherLevel: 'Tasmania',
          country: 'Australia',
        },
      ],
      popular: [
        {
          name: 'Bali',
          level: 'location',
          higherLevel: '',
          country: 'Indonesia',
        },
        {
          name: 'Hawaii',
          level: 'location',
          higherLevel: '',
          country: 'United States',
        },
        {
          name: 'Phuket',
          level: 'location',
          higherLevel: 'Phuket',
          country: 'Thailand',
        },
        {
          name: 'Khao Lak',
          level: 'location',
          alternativeNameForLink: 'Phang-Nga',
          higherLevel: '',
          country: 'Thailand',
        },
        {
          name: 'Koh Samui',
          level: 'location',
          higherLevel: 'Surat Thani',
          country: 'Thailand',
        },
        {
          name: 'Fiji',
          level: 'country',
        },
      ],
    },
    topDestinationsPages: [{
      text: 'Australia',
      id: 'pages-australia',
      highlighted: true,
      thumbnailId: 'opera-house_n1ogbi',
      url: {
        to: '/australia.d1',
      },
    },
    {
      text: 'New Zealand',
      id: 'destinations-new-zealand',
      highlighted: false,
      thumbnailId: 'opera-house_n1ogbi',
      url: {
        to: '/new-zealand.d1',
      },
    },
    {
      text: 'Bali',
      id: 'destinations-bali',
      highlighted: true,
      thumbnailId: 'rh0ytqckvmoo48qwrqpx',
      url: {
        to: '/bali.d1',
      },
    },
    {
      text: 'California',
      id: 'destinations-california',
      highlighted: false,
      thumbnailId: 'california_lmtvew',
      url: {
        to: '/california.d1',
      },
    },
    {
      text: 'Europe',
      id: 'destinations-europe',
      highlighted: false,
      thumbnailId: 'europe_u7p7dq',
      url: {
        to: '/europe.d1',
      },
    },
    {
      text: 'Fiji',
      id: 'destinations-fiji',
      highlighted: false,
      thumbnailId: 'fiji_nhtvw1',
      url: {
        to: '/fiji.d1',
      },
    },
    {
      text: 'Hawaii',
      id: 'destinations-hawaii',
      highlighted: false,
      thumbnailId: 'hawaii_i9owps',
      url: {
        to: '/hawaii.d1',
      },
    },
    {
      text: 'Hong Kong',
      id: 'destinations-hong-kong',
      highlighted: false,
      thumbnailId: 'hong_kong_zbuuef',
      url: {
        to: '/hong-kong.d1',
      },
    },
    {
      text: 'Japan',
      id: 'destinations-japan',
      highlighted: false,
      thumbnailId: 'japan_qvmuva',
      url: {
        to: '/japan.d1',
      },
    },
    {
      text: 'Maldives',
      id: 'destinations-maldives',
      highlighted: true,
      thumbnailId: 'maldives_lr4jbc',
      url: {
        to: '/maldives.d1',
      },
    },
    {
      text: 'Singapore',
      id: 'destinations-singapore',
      highlighted: false,
      thumbnailId: 'singapore_kavdtp',
      url: {
        to: '/singapore.d1',
      },
    },
    {
      text: 'South Pacific',
      id: 'destinations-south-pacific',
      highlighted: false,
      thumbnailId: 'south_pacific_qzsicb',
      url: {
        to: '/south-pacific.d1',
      },
    },
    {
      text: 'Thailand',
      id: 'destinations-thailand',
      highlighted: true,
      thumbnailId: 'thailand_zyqcm6',
      url: {
        to: '/thailand.d1',
      },
    },
    {
      text: 'Vietnam',
      id: 'destinations-vietnam',
      highlighted: false,
      thumbnailId: 'vietnam_gije3p',
      url: {
        to: '/vietnam.d1',
      },
    }],
    staticPages: [{
      type: 'support',
      text: staticPageMessages.support,
    },
    {
      type: 'termsAndConditions',
      text: staticPageMessages.termsAndConditions,
    },
    {
      type: 'bookingPolicies',
      text: staticPageMessages.bookingPolicies,
    }, {
      type: 'privacyPolicy',
      text: staticPageMessages.privacyPolicy,
    },
    {
      type: 'flights',
      text: staticPageMessages.directMaldivesFlights,
    }, {
      type: 'frequentFlyerPrograms',
      text: staticPageMessages.frequentFlyerPrograms,
    }, {
      type: 'bestPriceTerms',
      text: staticPageMessages.bestPriceGuarantee,
    }, {
      type: 'giftcardTermsAndConditions',
      text: staticPageMessages.luxuryEscapesGiftCards,
    }, {
      type: 'specialProductsPromotions',
      text: staticPageMessages.specialProductsPromotions,
    }, {
      type: 'refundPolicy',
      text: staticPageMessages.refundPolicy,
    }, {
      type: 'aboutUs',
      text: staticPageMessages.aboutUs,
    }, {
      type: 'bestPrice',
      text: staticPageMessages.bestPriceGuarantee,
    }, {
      type: 'careersAndCulture',
      text: staticPageMessages.careersAndCulture,
    }, {
      type: 'travelInsurance',
      text: staticPageMessages.travelProtection,
    }, {
      type: 'travelInsuranceCoverGenius',
      text: staticPageMessages.travelProtectionXcover,
    }, {
      type: 'contactUs',
      text: staticPageMessages.contactUs,
    }, {
      type: 'news',
      text: staticPageMessages.latestNews,
    }, {
      type: 'bnblLanding',
      text: staticPageMessages.buyNowChooseDatesLater,
    }, {
      type: 'twcLanding',
      text: staticPageMessages.tourWithConfidence,
    }, {
      type: 'ewcLanding',
      text: staticPageMessages.escapeWithConfidence,
    }, {
      type: 'affiliatePage',
      text: staticPageMessages.becomeAnAffiliatePartner,
    }],
    contentLinks: {
      termsAndConditions: { href: 'https://luxuryescapes.com/terms-and-conditions' },
      privacyPolicy: { href: 'https://luxuryescapes.com/privacy-policy' },
      refundPolicy: { href: 'https://luxuryescapes.com/refund-policy' },
      contactUs: { href: 'https://luxuryescapes.com/frequently-asked-questions#contact-form' },
      news: { href: 'https://luxuryescapes.com/news' },
      bestPriceTerms: { href: 'https://luxuryescapes.com/best-price-guarantee-terms' },
      bookingPolicies: { href: 'https://luxuryescapes.com/booking-policies' },
      bestPrice: { to: '/best-price-guarantee' },
    },
    bestPriceGuarantee: {
      title: 'Our Best Price Guarantee',
      content: 'Luxury Escapes has a Best Price on Earth Guarantee. If you find a better price online, we’ll match it plus more.',
      shortTitle: 'Best Price Guarantee',
      shortContent: 'Found a better price? We’ll match it… and more.',
      slug: 'best-price-guarantee',
    },
    whyBookWithUs: {
      type: 'original',
    },
    legalPagesSideBar: true,
    restrictPhoneNumbersToSupportedRegions: false,
    currencyRedirectEnabled: true,
    defaultRegion: 'AU',
    hasReferralProgram: false,
    search: {
      ...baseConfig.search,
      landingPageVideo: {
        au: '536192982',
        default: '544373017',
      },
    },
    logo: {
      icon: 'leagenthub',
      alt: 'LE Agent Hub',
      iconHeight: 35,
      iconWidth: 212,
      fillColor: '#000',
    },
    head: {
      metaTitleForDestinationProductPage: leAgentHubConfigMessages.headMetaTitle,
      defaultTitle: leAgentHubConfigMessages.headDefaultTitle,
      defaultDescription: leAgentHubConfigMessages.headDefaultDescription,
      brandOpenGraphImageId: '3or29n7ploh4qriq852j',
    },
    supportUrl: 'https://lehelpterminal.force.com/s/',
    hasGuestFrontPage: true,
    snowplow: {
      appId: 'www-le-agent',
    },
    signUp: {
      showPostcode: false,
    },
    logoImages: [
      {
        id: 'partner-logo-accor',
        name: 'accor',
        width: 110,
      },
      {
        id: 'partner-logo-marriot',
        name: 'marriott',
        width: 110,
      },
      {
        id: 'partner-logo-ihg',
        name: 'ihg',
        width: 110,
      },
      {
        id: 'partner-logo-shangri-la',
        name: 'shangri-la',
        width: 110,
      },
      {
        id: 'partner-logo-hyatt',
        name: 'hyatt',
        width: 110,
      },
      {
        id: 'partner-logo-outrigger',
        name: 'outrigger',
        width: 110,
      },
      {
        id: 'partner-logo-kempinski',
        name: 'kempinski',
        width: 110,
      },
      {
        id: 'partner-logo-melia',
        name: 'melia',
        width: 110,
      },
      {
        id: 'partner-logo-como',
        name: 'como',
        width: 110,
      },
      {
        id: 'partner-logo-trafalgar',
        name: 'trafalgar',
        width: 110,
      },
      {
        id: 'partner-logo-insight-vacations',
        name: 'insight-vacations',
        width: 110,
      },
      {
        id: 'partner-logo-abercrombie-kent',
        name: 'abercrombie-kent',
        width: 110,
      },
      {
        id: 'partner-logo-royal-caribbean',
        name: 'royal-caribbean',
        width: 110,
      },
      {
        id: 'partner-logo-princess-cruises',
        name: 'princess-cruises',
        width: 110,
      },
      {
        id: 'partner-logo-cunard',
        name: 'cunard',
        width: 110,
      },
      {
        id: 'partner-logo-virgin',
        name: 'virgin',
        width: 110,
      },
    ],
    agentHub: {
      isEnabled: true,
      payoutSetupUrl: `${env.AGENT_PORTAL_URL}/settings?display-payout-details-dialog=true`,
      termsUrl: 'https://luxuryescapes.com/au/terms-and-conditions',
      policyUrl: 'https://luxuryescapes.com/au/privacy-policy',
      supportEmail: 'agenthubsupport@luxuryescapes.com',
      displayShortName: 'Agent Hub',
      displayFullName: 'LE Agent Hub',
      isCVFREnabled: false,
      basePath: 'agent-hub',
    },
    paymentMethodLimitsByRegion: {},
    paymentMethodsByRegion: {},
    // Copied from ./luxuryescapes.ts
    loyalty: {
      programDisplayName: 'Société',
      pointsUnit: 'point',
      pointsUnitAbbreviation: 'pt',
      bonusPointsUnit: 'bonus point',
      bonusPointsUnitAbbreviation: 'bonus pt',
      statusCreditsUnit: 'status credit',
      tiers: {
        bronze: { displayName: 'Bronze' },
        silver: { displayName: 'Silver' },
        gold: { displayName: 'Gold' },
        platinum: { displayName: 'Platinum' },
      },
      tiersOrder: ['bronze', 'silver', 'gold', 'platinum'],
    },
    multiClickUnsubPage: { copyVariant: 'luxuryEscapes' },
  })
}
